import { Chart, registerables } from 'chart.js';

Chart.register(...registerables);

function themeColors() {
  const dark = document.documentElement.classList.contains('dark');
  return {
    dark,
    text: dark ? '#9ca3af' : '#6b7280',
    grid: dark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
    point: dark ? '#60a5fa' : '#2563eb',
    line: dark ? '#a78bfa' : '#7c3aed',
    fill: dark ? 'rgba(167,139,250,0.10)' : 'rgba(124,58,237,0.08)',
    tooltipBg: dark ? '#1f2937' : '#111827',
    // Achtergrondbanden per conditieklasse (koel/gematigd/warm)
    bandKoel: dark ? 'rgba(96,165,250,0.08)' : 'rgba(59,130,246,0.07)',
    bandGematigd: dark ? 'rgba(52,211,153,0.07)' : 'rgba(16,185,129,0.06)',
    bandWarm: dark ? 'rgba(248,113,113,0.09)' : 'rgba(239,68,68,0.07)',
  };
}

function baseComponent(render) {
  return {
    _chart: null,
    _observer: null,

    init() {
      this.$nextTick(() => this.render());
      this._observer = new MutationObserver(() => this.render());
      this._observer.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ['class'],
      });
    },

    destroy() {
      this._observer?.disconnect();
      this._chart?.destroy();
    },

    render,
  };
}

function datumKort(iso) {
  const dt = new Date(String(iso).replace(' ', 'T'));
  return Number.isNaN(dt.getTime())
    ? String(iso)
    : dt.toLocaleDateString('nl-BE', { day: 'numeric', month: 'short', year: 'numeric' });
}

/**
 * Spreidingsgrafiek zweetverlies (L) t.o.v. temperatuur (°C), met
 * achtergrondbanden per conditieklasse en datum + intensiteit in de hover.
 *
 * punten: [{ temperatuur, zweetverlies, datum, intensiteit }]
 * grenzen: { koel_max_c, warm_min_c }
 * labels: { zweetverlies, temperatuur, koel, gematigd, warm }
 */
export function zweettestScatterChart(punten, grenzen, labels) {
  const component = baseComponent(function () {
    this._chart?.destroy();
    this._chart = null;

    const canvas = this.$refs.chart;
    if (!canvas || !punten.length) return;

    const t = themeColors();
    const koelMax = parseFloat(grenzen.koel_max_c);
    const warmMin = parseFloat(grenzen.warm_min_c);

    const temps = punten.map((p) => parseFloat(p.temperatuur));
    const xMin = Math.min(Math.floor(Math.min(...temps) - 2), koelMax - 2);
    const xMax = Math.max(Math.ceil(Math.max(...temps) + 2), warmMin + 2);

    const conditiebanden = {
      id: 'conditiebanden',
      beforeDatasetsDraw(chart) {
        const { ctx, chartArea, scales } = chart;
        if (!chartArea || !scales.x) return;
        const px = (v) => Math.min(Math.max(scales.x.getPixelForValue(v), chartArea.left), chartArea.right);
        const banden = [
          [chartArea.left, px(koelMax), t.bandKoel],
          [px(koelMax), px(warmMin), t.bandGematigd],
          [px(warmMin), chartArea.right, t.bandWarm],
        ];
        ctx.save();
        for (const [van, tot, kleur] of banden) {
          if (tot <= van) continue;
          ctx.fillStyle = kleur;
          ctx.fillRect(van, chartArea.top, tot - van, chartArea.bottom - chartArea.top);
        }
        ctx.restore();
      },
    };

    this._chart = new Chart(canvas, {
      type: 'scatter',
      data: {
        datasets: [
          {
            data: punten.map((p) => ({
              x: parseFloat(p.temperatuur),
              y: parseFloat(p.zweetverlies),
              datum: p.datum,
              intensiteit: p.intensiteit,
            })),
            backgroundColor: t.point,
            pointRadius: 5,
            pointHoverRadius: 7,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: {
            backgroundColor: t.tooltipBg,
            padding: 10,
            cornerRadius: 6,
            callbacks: {
              title: (items) => datumKort(items[0].raw.datum),
              label: (ctx) => {
                const regels = [
                  `${labels.zweetverlies}: ${ctx.parsed.y.toFixed(2)} L`,
                  `${labels.temperatuur}: ${ctx.parsed.x.toFixed(1)} °C`,
                ];
                if (ctx.raw.intensiteit) regels.push(ctx.raw.intensiteit);
                return regels;
              },
            },
          },
        },
        scales: {
          x: {
            min: xMin,
            max: xMax,
            title: { display: true, text: `${labels.temperatuur} (°C)`, color: t.text },
            ticks: { color: t.text },
            grid: { display: false },
          },
          y: {
            beginAtZero: true,
            title: { display: true, text: `${labels.zweetverlies} (L)`, color: t.text },
            ticks: { color: t.text },
            grid: { color: t.grid },
          },
        },
      },
      plugins: [conditiebanden],
    });
  });
  return component;
}

/**
 * Lijngrafiek natriumconcentratie (mg/L) over tijd. De view toont de grafiek
 * pas vanaf 2 natriumtests.
 *
 * punten: [{ datum, waarde }] oplopend op datum
 */
export function zweettestNatriumChart(punten, label) {
  const component = baseComponent(function () {
    this._chart?.destroy();
    this._chart = null;

    const canvas = this.$refs.chart;
    if (!canvas || punten.length < 2) return;

    const t = themeColors();

    this._chart = new Chart(canvas, {
      type: 'line',
      data: {
        labels: punten.map((p) => p.datum),
        datasets: [
          {
            label,
            data: punten.map((p) => parseFloat(p.waarde)),
            borderColor: t.line,
            backgroundColor: t.fill,
            pointBackgroundColor: t.point,
            pointRadius: 3,
            pointHoverRadius: 5,
            borderWidth: 2,
            fill: true,
            tension: 0.3,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: 'index', intersect: false },
        plugins: {
          legend: { display: false },
          tooltip: {
            backgroundColor: t.tooltipBg,
            padding: 10,
            cornerRadius: 6,
            callbacks: {
              title: (items) => datumKort(items[0].label),
              label: (ctx) => `${Math.round(ctx.parsed.y)} mg/L`,
            },
          },
        },
        scales: {
          x: {
            ticks: {
              color: t.text,
              maxTicksLimit: 10,
              callback: function (val) {
                return datumKort(this.getLabelForValue(val));
              },
            },
            grid: { display: false },
          },
          y: {
            beginAtZero: true,
            title: { display: true, text: 'mg/L', color: t.text },
            ticks: { color: t.text },
            grid: { color: t.grid },
          },
        },
      },
    });
  });
  return component;
}
